// Lightweight URL <-> tab mapping for the tab-based navigation in App.vue.
//
// The app has no vue-router: navigation swaps an `activeTab` ref in-memory.
// That left the browser URL stuck at "/" on every screen, so pages couldn't be
// bookmarked/refreshed/shared and the Open Banking redirect (which comes back
// to "/settings") had no real route to land on. These helpers let App.vue keep
// the URL path in sync with the active tab using the History API.

// Tab id -> URL path. Dashboard is the landing tab, so it owns the root path.
export const TAB_PATHS: Record<string, string> = {
  dashboard: "/",
  assistant: "/assistant",
  transactions: "/transactions",
  budgets: "/budgets",
  insights: "/insights",
  grocery: "/grocery",
  household: "/household",
  settings: "/settings",
}

// Old paths that used to be tabs. Redirect them to their new home so
// bookmarks and browser history keep working after the IA change.
const LEGACY_TAB_PATHS: Record<string, string> = {
  // The standalone Paychecks tab was folded into Budgets → Income.
  "/income": "budgets",
}

// Paths owned by other flows (server callbacks / modal handlers), NOT tabs.
// We must never treat these as a tab or rewrite them, or we'd break the magic
// link handler, the Open Banking callback guard, and the Vipps return URLs.
const RESERVED_PATHS = new Set<string>([
  "/accept-invitation",
  "/subscription/callback",
  "/subscription/manage",
  "/login-callback",
])

const PATH_TO_TAB: Record<string, string> = Object.fromEntries(
  Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab]),
)

// URL path for a tab id (defaults to root for unknown ids).
export function pathForTab(tabId: string): string {
  return TAB_PATHS[tabId] ?? "/"
}

// Tab id for a URL path, or null when the path is reserved/unknown so the
// caller can fall back to the default tab without clobbering the URL.
export function tabForPath(path: string): string | null {
  if (RESERVED_PATHS.has(path)) return null
  // Normalise a trailing slash (e.g. "/settings/") to the canonical path.
  const normalized = path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path
  return PATH_TO_TAB[normalized] ?? LEGACY_TAB_PATHS[normalized] ?? null
}
